<template>
  <div class="sb-menu" v-bind="$attrs">
    <slot
      name="button"
      :is-open="isOpen"
      :menu-list-id="menuListId"
      :menu-button-id="menuButtonId"
      v-bind="$attrs"
    />
    <slot />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, provide, onUpdated } from 'vue'
import { randomString, canUseDOM } from '../../utils'

import { useDropdown } from '../Dropdown/use-dropdown'

export type SbMenuProps = {
  /**
   * Prevent the menu from closing when clicking outside
   */
  preventClose?: boolean

  /**
   * Control when the menu is open or closed
   */
  modelValue?: boolean

  /**
   * Focus the first item when the menu is open
   */
  focusWhenClose?: boolean
}

const props = withDefaults(defineProps<SbMenuProps>(), {
  focusWhenClose: true,
})

const emit = defineEmits<{
  /**
   * emit close event when the menu is closed
   *
   * @event close
   */
  (e: 'close'): void

  /**
   * emit the modelValue when the menu is open or closed
   *
   * @event update:modelValue
   * @property {boolean} state
   */
  (e: 'update:modelValue', state: boolean): void

  /**
   * emit open event when the menu is opened
   *
   * @event open
   */
  (e: 'open'): void
}>()

const root = ref<HTMLUListElement | null>(null)
const {
  activatedMenuItemId,
  isOpen,
  loadListItems,
  focusOnLastItem,
  focusOnFirstItem,
  handleKeydownOnList,
} = useDropdown({
  root,
})

const activeIndex = ref(-1)
const focusableCount = ref(0)
const menuListId = `sb-menu-list-${randomString(4)}`
const menuButtonId = `sb-menu-button-${randomString(4)}`

const menuContext = computed(() => () => ({
  activatedMenuItemId: activatedMenuItemId.value,
  isOpen: isOpen.value,
  preventClose: props.preventClose,
  menuListId,
  menuButtonId,
  activeIndex: activeIndex.value,
  focusableCount: focusableCount.value,
  closeMenu,
  openMenu,
  toggleMenu,
  focusOnFirstItem,
  focusOnLastItem,
  handleKeydownOnList,
  focusButton,
}))

provide('menuContext', menuContext.value)

watch(isOpen, (state) => {
  emit('update:modelValue', state)
})

watch(
  () => props.modelValue,
  (state) => {
    if (state) {
      focusOnFirstItem()
    } else {
      closeMenu()
    }
  },
)

onMounted(() => {
  root.value = document.querySelector(`#${menuListId}`)
  loadListItems()
  isOpen.value = props.modelValue || false
})

onUpdated(() => {
  if (root.value) {
    loadListItems()
  }
})

const closeMenu = () => {
  if (!isOpen.value) return
  isOpen.value = false
  if (props.focusWhenClose) activeIndex.value = -1
  focusButton()
  emit('close')
}

const openMenu = () => {
  if (isOpen.value) return
  isOpen.value = true
  emit('open')
}

const toggleMenu = () => {
  if (isOpen.value) {
    closeMenu()
  } else {
    openMenu()
  }
}

const focusButton = () => {
  if (canUseDOM && document.querySelector(`#${menuButtonId}`)) {
    document.querySelector<HTMLButtonElement>(`#${menuButtonId}`)?.focus()
  }
}

defineExpose({
  /**
   * Triggers the closeMenu method
   */
  closeMenu,

  /**
   * Triggers the openMenu method
   */
  openMenu,

  /**
   * Triggers the toggleMenu method
   */
  toggleMenu,

  /**
   * Triggers method that focus on the menu button
   */
  focusButton,
})
</script>
